import React, { useId, useMemo, useRef, useState } from 'react';

/**
 * Intraday sparkline with a hover readout.
 *
 * `expectedPoints` is the typical number of bars in a full trading day for this
 * series — the x-axis is scaled against it (not against how many points we
 * happen to have) so early in the day the line only occupies the elapsed
 * portion of the width instead of stretching edge-to-edge. The y-axis
 * auto-scales to this series' own min/max so its intraday movement is visible.
 *
 * On hover it snaps a crosshair + dot to the nearest point and shows its value
 * (and time, when a parallel `times` array of ISO timestamps is supplied).
 */
export default function Sparkline({
  points,
  times,
  color = '#22c55e',
  expectedPoints,
  displayMode = 'number',
  width = 90,
  height = 28,
}) {
  const gradientId = useId();
  const wrapRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null);

  const { linePath, areaPath, coords, xDenominator } = useMemo(() => {
    if (!points?.length) return { linePath: '', areaPath: '', coords: [], xDenominator: 1 };
    const max = Math.max(...points);
    const min = Math.min(...points);
    const span = Math.max(1e-9, max - min);
    const denom = Math.max(1, (expectedPoints || points.length) - 1);
    const c = points.map((v, i) => [
      (i / denom) * 100,
      100 - ((v - min) / span) * 100,
    ]);
    const line = c.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x},${y}`).join(' ');
    const lastX = c[c.length - 1][0];
    const firstX = c[0][0];
    return { linePath: line, areaPath: `${line} L ${lastX},100 L ${firstX},100 Z`, coords: c, xDenominator: denom };
  }, [points, expectedPoints]);

  const formatValue = (v) => {
    if (v == null) return '—';
    if (displayMode === 'percent') return `${Number(v).toFixed(2)}%`;
    return Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleMove = (e) => {
    if (!points?.length || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const fraction = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const idx = Math.min(points.length - 1, Math.max(0, Math.round(fraction * xDenominator)));
    setHoverIdx(idx);
  };

  const clear = () => setHoverIdx(null);

  const empty = !linePath;
  const hover = hoverIdx != null && coords[hoverIdx] ? { idx: hoverIdx, x: coords[hoverIdx][0], y: coords[hoverIdx][1] } : null;

  return (
    <div
      ref={wrapRef}
      onMouseMove={handleMove}
      onMouseLeave={clear}
      onTouchStart={handleMove}
      onTouchMove={handleMove}
      onTouchEnd={clear}
      style={{ position: 'relative', width, height, touchAction: 'none' }}
    >
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%', overflow: 'visible', display: 'block' }}>
        {!empty && (
          <>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity="0.35" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" />
            <path
              d={linePath}
              fill="none"
              stroke={color}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
            {hover && (
              <line
                x1={hover.x}
                y1="0"
                x2={hover.x}
                y2="100"
                stroke="var(--muted, #8b96a5)"
                strokeWidth="1"
                strokeDasharray="3 3"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </>
        )}
      </svg>

      {/* Dot + tooltip are HTML overlays (percent-positioned) so the non-uniform
          SVG scaling doesn't distort them. */}
      {hover && (
        <>
          <div
            style={{
              position: 'absolute', left: `${hover.x}%`, top: `${hover.y}%`,
              width: 7, height: 7, borderRadius: '50%', background: color,
              boxShadow: '0 0 0 2px var(--surface, #0b0f14)',
              transform: 'translate(-50%, -50%)', pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'absolute', left: `${hover.x}%`, top: 0,
              transform: 'translate(-50%, calc(-100% - 6px))',
              background: 'var(--card, #1c2333)', border: '1px solid var(--border, rgba(255,255,255,0.12))',
              borderRadius: 6, padding: '4px 8px', pointerEvents: 'none',
              whiteSpace: 'nowrap', zIndex: 20, textAlign: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text, #e6e9ef)' }}>
              {formatValue(points[hover.idx])}
            </div>
            {times?.[hover.idx] && (
              <div style={{ fontSize: 10, color: 'var(--muted, #8b96a5)' }}>
                {formatTime(times[hover.idx])}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
